// Imports ------------------------------------------------------------------->

import {Component} from "../Component";
import {element} from "../CustomElement";

import {inject} from "../../services/di";
import {AuthenticationService} from "../../services/AuthenticationService";
import {ConfigService} from "../../services/ConfigService";
import {icons} from "../../icons/Icons";
import {i18n} from "../../i18n/I18n";
import {Link} from "../flipp-link/Link";
import {AuthProvider} from "../../definitions/CommonDefinitions";
import {EventService} from "../../services/EventService";
import {AccountsService} from "../../services/AccountsService";
import {Country, LocationService} from "../../services/LocationService";
import {ToastService} from "../../services/ToastService";
import {LinkService} from "../../services/LinkService";
import {
	handleFormInputsChange,
	handleLogin,
	handleResendActionRequest,
	handleSubmitRequest,
	hasEmptyFields,
	setDocumentTitle,
} from "./Login.helper";
import {FormInput} from "../flipp-form-input/FormInput";
import {SocialIconsContainer} from "./SocialIconsContainer";
import {WarningToast} from "./WarningToast";

@element("flipp-signin-page")
export class SigninPage extends Component<{}, void> {

	@inject config: ConfigService;
	@inject auth: AuthenticationService;
	@inject eventService: EventService;
	@inject accounts: AccountsService;
	@inject location: LocationService;
	@inject toast: ToastService;

	refs: {
		submitForm: HTMLFormElement;
		submitBtn: HTMLElement;
		spinner: HTMLElement;
		eye: HTMLElement;
		resendCodeBtn: HTMLElement;
		emailInput: FormInput;
		passwordInput: FormInput;
		submitBtnText: HTMLElement;
		resendCodeSpinner: HTMLElement;
		responseErrorMsg: HTMLElement;
		underButton: HTMLElement;
		termsCheckbox: HTMLElement;
		privacyCheckbox: HTMLElement;
		toast: WarningToast;
		socialIcons: SocialIconsContainer;
	};

	state = {
		email: "",
		password: "",
		terms: false,
		privacy: false,
	};

	onCreate() {
		setDocumentTitle(i18n("signin_header"));
	}

	onAttach() {
		this.state.email = "";
		this.state.password = "";
		this.state.terms = false;
		this.state.privacy = false;
		this.refs.resendCodeSpinner.style.display = "none";
		this.refs.termsCheckbox.addEventListener("input", this.handleCheckboxChanged);
		this.refs.privacyCheckbox.addEventListener("input", this.handleCheckboxChanged);
	}

	onDetach() {
		this.refs.termsCheckbox.removeEventListener("input", this.handleCheckboxChanged);
		this.refs.privacyCheckbox.removeEventListener("input", this.handleCheckboxChanged);
	}

	handleCheckboxChanged = (e: any) => {
		Object.assign(this.state, {[e.target.name]: e.target.checked});

		if (this.state.terms && this.state.privacy) {
			this.refs.toast.classList.remove("show");
			this.refs.socialIcons.setAttribute("isDisabled", "false");
		} else {
			this.refs.socialIcons.setAttribute("isDisabled", "true");
		}
	}

	render() {
		return (
			<main>
				<div class="top-bar" style={{backgroundColor: this.config.getThemeColor()}}>
					<Link to="listing" class="flipp-logo"title="Flipp">
						{icons.flippLogoWhite}
					</Link>
				</div>
				<div class="content">
					<div>
						<WarningToast ref="toast"/>
					</div>
					<div class="form-container">
						<div class="header">{i18n("signin_header")}</div>
						<div class="account-text">
							{i18n("signin_no_account")}
							<Link href="/signup">{i18n("signup")}</Link>.
						</div>
						<form
							name="submitForm"
							class="form"
							ref="submitForm"
							onSubmit={this.handleSubmit}
						>
							<FormInput ref="emailInput" name={"email"} handleChange={this.handleChange} type={"email"} placeholder={i18n("email")}/>
							<FormInput ref="passwordInput" name={"password"} handleChange={this.handleChange} type={"password"} placeholder={i18n("password")}/>
							<div class="forgot-password-link-container">
								<div class="forgot-password-link">
									<Link href="/forgot_password" handler={this.onForgotPasswordLinkClick.bind(this)}>{i18n("forgot_my_password")}</Link>
								</div>
							</div>
							<div class="checkbox-wrapper">
								<input
									id="terms"
									name="terms"
									ref="termsCheckbox"
									type="checkbox"
								/>
								<label for="terms" >{i18n("accept_terms")}<a href={i18n("terms_url")} target="_blank">{i18n("terms_of_use")}</a></label>
							</div>
							<div class="checkbox-wrapper">
								<input
									id="privacy"
									name="privacy"
									ref="privacyCheckbox"
									type="checkbox"
								/>
								<label for="privacy">{i18n("accept_privacy")}<a href={i18n("privacy_url")} target="_blank">{i18n("privacy_policy")}</a></label>
							</div>
							<div class="button-container">
								<button ref="submitBtn" class="flipp-button">
									<div ref="spinner" class="spinner hide"></div>
									<div ref="submitBtnText" class="submit-button-text">
										<span>{i18n("signin")}</span>
									</div>

								</button>
								<div ref="underButton" class="under-button-error">
									<p ref="responseErrorMsg" class="error-msg"></p>
									<div ref="resendCodeBtn" onClick={this.handleResendAction} class="under-button-action resend resend-code hide">
										{i18n("signin_resend_verification_code")}
									</div>
									<div ref="resendCodeSpinner">
										<div class="spinner"></div>
									</div>
								</div>
								<div class="divider">
									<div class="line"/><div class="text">{i18n("or")}</div><div class="line"/>
								</div>
								<SocialIconsContainer ref="socialIcons" isDisabled={"true"} buttonClick={this.onSocialButtonClick}/>
							</div>
						</form>
					</div>
					<div class="picture">
						<img src={`https://f.wishabi.net/creative/Distribution/images/login-picture.svg`} alt="" />
					</div>
				</div>
			</main>
		);
	}

	private signin = async () => {
		await this.auth.login(AuthProvider.EMAIL, this, this.state);
		const redirectUri = this.location.country === Country.CANADA && !this.auth.user.token.email_subscribed ? "opt_in" : "weekly_ads";
		handleLogin(this.auth.user.token.user_id, this, redirectUri);
	}

	private onForgotPasswordLinkClick(e: any) {
		if (!this.state.terms || !this.state.privacy) {
			this.refs.toast.classList.add("show");
			e.preventDefault();
		}
	}

	private handleChange = (e: any) => {
		this.state = handleFormInputsChange(this.state, this.refs, e);
		this.refs.resendCodeBtn.classList.remove("show");
	}

	private onSocialButtonClick = () => {
		if (!this.state.terms || !this.state.privacy) {
			this.refs.toast.classList.add("show");
		}
	}

	private handleSubmit = async(e: any) => {
		e.preventDefault();
		this.refs.responseErrorMsg.textContent = "";

		if (e.submitter.className.trim() === "google" || e.submitter.className.trim() === "facebook") {
			return;
		}

		if (!this.state.terms || !this.state.privacy) {
			this.refs.toast.classList.add("show");
			return;
		}

		this.refs.toast.classList.remove("show");

		if (hasEmptyFields(this.state)) {
			if (this.state.email.length === 0) {
				this.refs.emailInput.errorMessage = i18n("enter_email_error");
			}
			if (this.state.password.length === 0) {
				this.refs.passwordInput.errorMessage = i18n("enter_password_error");
			}
		} else {
			await handleSubmitRequest(this.refs, this.signin, (body) => {
				if (body.code === "USER_NOT_CONFIRMED") {
					this.refs.resendCodeBtn.classList.remove("hide");
					this.refs.responseErrorMsg.textContent = i18n("USER_NOT_CONFIRMED");
				}
			});
		}
	}

	private handleResendAction = async(e: any) => {
		await handleResendActionRequest(e, this.refs, this.state.email, () => {
			const linkService = LinkService.getInstance();
			const uri = `confirm_email?resend=true&email=${encodeURIComponent(this.state.email)}`;
			linkService.execute(uri, this);
		});
	}
}
